/**
 * HomePage — the simple-mode landing dashboard.
 *
 * Greeting + three big action cards (Sign File / Prove Idea / Create
 * Document) + a 5-card recent-proofs gallery + activity stats strip
 * + industry chip strip. Mobile-first grid that stacks below `sm`.
 *
 * On the user's very first signed proof (count transitions 0 → 1),
 * the topmost gallery card animates with the `settle` verb instead
 * of `arrive` and gets a brief emerald sweep + ring — a one-time
 * celebration moment per the redesign spec.
 *
 * Per [docs/DASHBOARD-HOME-EXPLORE-IMPLEMENTATION.md §2.2].
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FileText, Lightbulb, FileSignature, ArrowRight } from 'lucide-react';

import { ActionCard } from '@/components/ActionCard';
import { ProofStats } from '@/components/ProofStats';
import { ProofGalleryCard } from '@/components/ProofGalleryCard';
import { EmptyHomeHint } from '@/components/EmptyHomeHint';
import { IndustryExplorer } from '@/components/IndustryExplorer';
import { useAuthStore } from '@/stores/authStore';
import { useProofs } from '@/hooks/useProofs';
import { stagger, arrive } from '@/lib/motion';

const PROOFS_ON_HOME = 5;

export function HomePage() {
  const { t } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const reduce = useReducedMotion();
  const firstName =
    (user?.name ?? '').split(' ')[0] || t('greeting.fallback_name');

  const { data: proofs = [], isFetched } = useProofs();
  const recent = proofs.slice(0, PROOFS_ON_HOME);
  const hasProofs = recent.length > 0;

  // First-proof celebration: keep a ref to the previous count.
  // When count transitions 0 → ≥1, the topmost ProofGalleryCard
  // animates with `settle` instead of plain `arrive`.
  const prevCountRef = useRef<number | null>(null);
  const justSignedFirst =
    isFetched &&
    prevCountRef.current === 0 &&
    proofs.length === 1;

  useEffect(() => {
    if (isFetched) prevCountRef.current = proofs.length;
  }, [isFetched, proofs.length]);

  return (
    <>
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {t('greeting.hi', { name: firstName })}
        </h1>
        <p className="mt-2 text-base text-ink-muted">
          {t('greeting.prompt')}
        </p>
      </header>

      {/* ── Three action cards — hover lift already in ActionCard.
          `exactOptionalPropertyTypes` rejects `variants={undefined}`,
          so the reduced-motion path is a conditional-spread of the
          motion props instead of a ternary on the prop value. ── */}
      <motion.section
        aria-label={t('greeting.prompt')}
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        {...(reduce ? {} : { variants: stagger, initial: 'hidden', animate: 'visible' })}
      >
        <motion.div {...(reduce ? {} : { variants: arrive })}>
          <ActionCard
            to="/sign-file"
            icon={<FileText className="h-6 w-6" />}
            title={t('actions.sign_file.title')}
            description={t('actions.sign_file.description')}
            cta={t('actions.sign_file.cta')}
          />
        </motion.div>
        <motion.div {...(reduce ? {} : { variants: arrive })}>
          <ActionCard
            to="/prove-idea"
            icon={<Lightbulb className="h-6 w-6" />}
            title={t('actions.prove_idea.title')}
            description={t('actions.prove_idea.description')}
            cta={t('actions.prove_idea.cta')}
          />
        </motion.div>
        <motion.div {...(reduce ? {} : { variants: arrive })}>
          <ActionCard
            to="/create-document"
            icon={<FileSignature className="h-6 w-6" />}
            title={t('actions.create_document.title')}
            description={t('actions.create_document.description')}
            cta={t('actions.create_document.cta')}
          />
        </motion.div>
      </motion.section>

      {/* ── Empty state arrow (only when zero proofs) ── */}
      {!hasProofs && isFetched ? <EmptyHomeHint /> : null}

      {/* ── Stats strip (only when ≥1 proof) ── */}
      {hasProofs ? <ProofStats proofs={proofs} /> : null}

      {/* ── Recent proofs gallery (5 cards) ── */}
      {hasProofs ? (
        <motion.section
          aria-labelledby="recent-heading"
          className="mt-12"
          {...(reduce ? {} : { variants: stagger, initial: 'hidden', animate: 'visible' })}
        >
          <div className="mb-3 flex items-baseline justify-between">
            <h2
              id="recent-heading"
              className="text-base font-semibold tracking-tight"
            >
              {t('recent.title')}
            </h2>
            <Link
              to="/vault"
              className="inline-flex items-center gap-1 text-sm font-medium text-spark hover:underline"
            >
              {t('recent.view_all')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {recent.map((proof, i) => (
              <ProofGalleryCard
                key={proof.id}
                proof={proof}
                celebrate={justSignedFirst && i === 0}
              />
            ))}
          </div>
        </motion.section>
      ) : null}

      {/* ── Industry chip strip — preserved from current Home ── */}
      <IndustryExplorer />
    </>
  );
}
